import React from "react";
import DataTableView from "app/shared/components/data-table/data-table.web";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import { useTranslation } from "react-i18next";
import { EBINQUIRY, USERPROFILERESPONSE } from "app/shared/services/services-model.type";
import { Block } from "app/shared/components/blocks/block";
import EmptyBox from "app/shared/components/empty-box/empty-box";
import { FlatList, ListRenderItem, StyleSheet } from "react-native";
import { Constant } from "app/shared/constant/constant";
import { Paragraph, TouchableRipple, useTheme } from "react-native-paper";
import { spacing } from "app/shared/styles/spacing";
import { formatCurrency } from "app/shared/util/format-currency";

export interface DataTransHistoryTableProps {
  ADDRESS?: string;
  ADDITIONAL_FEE_AMOUNT?: string;
  CREDIT_AMOUNT?: string;
  DEBIT_AMOUNT?: string;
  FEE_TYPE?: string;
  MAIN_FEE_VAT_AMOUNT?: string;
  OTHER_FEE_AMOUNT?: string;
  TOTAL_FEE_AMOUNT?: string;
  VAT_FEE_AMOUNT?: string;
  CORE_STATUS?: string;
  CORE_TRANSACTION_DATE?: string;
  CREDIT_ACCOUNT_NAME?: string;
  CREDIT_ACCOUNT_NUMBER?: string;
  CREDIT_BANK_NAME?: string;
  CREDIT_BRANCH_NAME?: string;
  DEBIT_ACCOUNT_CURRENCY?: string;
  DEBIT_ACCOUNT_NAME?: string;
  DEBIT_ACCOUNT_NUMBER?: string;
  DEBIT_BANK_NAME?: string;
  DEBIT_BRANCH_NAME?: string;
  RQSTSEQ?: string;
  STATUS?: string;
  TRANSACTION_ALIAS?: string;
  TRANSACTION_AMOUNT?: string;
  TRANSACTION_DATE?: string;
  TRANSACTION_DESCRIPTION?: string;
  TRANSACTION_POSTING?: string;
  TYPE?: string;
  USERPROFILE?: USERPROFILERESPONSE;
  id?: string;
  status_name?: string;
  CURRENCY_TRANSACTION_AMOUNT?: string;
  CURRENCY_TOTAL_FEE_AMOUNT?: string;
  FORMAT_TRANSACTION_DATE?: string;
}

interface TransactionTableProps {
  onChangeData: (data: EBINQUIRY[]) => void;
  data?: DataTransHistoryTableProps[];
  isShow?: boolean;
}

function TransactionTable(props: TransactionTableProps) {
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();
  const { colors, fonts } = useTheme();

  const { data = [], onChangeData, isShow = false } = props;
  const [dataTable, setDataTable] = React.useState<DataTransHistoryTableProps[]>([]);
  const [arrayExpand, setArrayExpand] = React.useState<Array<boolean>>([]);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        displayShow: { display: isShow ? "flex" : "none" },
        textBold: { ...fonts.medium, color: colors.text, paddingVertical: 0 },
        text: { ...fonts.regular, color: colors.text, paddingVertical: 0 },
        label: { flex: 1, minWidth: "30%" },
        value: { textAlign: "right" },
      }),
    [colors.text, fonts.medium, fonts.regular, isShow],
  );

  const onPressExpand = (_index: number) => {
    const _arrayExpand: Array<boolean> = [];
    arrayExpand.forEach((value, index) => {
      let status: boolean = false;
      if (index === _index) status = !value;
      _arrayExpand.push(status);
      return value;
    });
    console.log("_arrayExpand new: ", _arrayExpand);
    setArrayExpand(_arrayExpand);
  };

  React.useEffect(() => {
    onChangeData(dataTable);
    const _arrayExpand: Array<boolean> = [];
    dataTable.forEach((_item) => {
      _arrayExpand.push(false);
    });
    setArrayExpand(_arrayExpand);
  }, [dataTable, onChangeData]);

  React.useEffect(() => {
    setDataTable(data || []);
  }, [data]);

  const renderItemDetail: ListRenderItem<DataTransHistoryTableProps> = ({ item, index }) => (
    <React.Fragment>
      <TouchableRipple key={`${item.id}` + index} onPress={() => onPressExpand(index)}>
        <Block pv={spacing.small} ph={spacing.medium} bg={index % 2 === 0 ? colors.surface : colors.backgroundForm} br={8}>
          <Block row spaced>
            <Paragraph style={[styles.textBold, styles.label]}>{t("ACC_SO_THAM_CHIEU")}</Paragraph>
            <Paragraph style={[styles.textBold, styles.value]}>{item.RQSTSEQ}</Paragraph>
          </Block>
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_NGAY_LAP_LENH")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>{item.FORMAT_TRANSACTION_DATE}</Paragraph>
          </Block>
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_SO_TIEN")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>{formatCurrency(item.TRANSACTION_AMOUNT)}</Paragraph>
          </Block>
          {arrayExpand[index] === true ? (
            <>
              <Block row spaced>
                <Paragraph style={[styles.text, styles.label]}>{t("ACC_PHI_GD")}</Paragraph>
                <Paragraph style={[styles.text, styles.value]}>{formatCurrency(item.TOTAL_FEE_AMOUNT)}</Paragraph>
              </Block>
              <Block row spaced>
                <Paragraph style={[styles.text, styles.label]}>{t("ACC_TK_NHAN")}</Paragraph>
                <Paragraph style={[styles.text, styles.value]}>{item?.CREDIT_ACCOUNT_NUMBER}</Paragraph>
              </Block>
              <Block row spaced>
                <Paragraph style={[styles.text, styles.label]}>{t("ACC_TEN_NGUOI_NHAN")}</Paragraph>
                <Paragraph style={[styles.text, styles.value]}>{item?.CREDIT_ACCOUNT_NAME}</Paragraph>
              </Block>
              <Block row spaced>
                <Paragraph style={[styles.text, styles.label]}>{t("ACC_NGAN_HANG_NHAN")}</Paragraph>
                <Paragraph style={[styles.text, styles.value]}>{item?.CREDIT_BANK_NAME}</Paragraph>
              </Block>
              <Block row spaced>
                <Paragraph style={[styles.text, styles.label]}>{t("ACC_MO_TA")}</Paragraph>
                <Paragraph style={[styles.text, styles.value]}>{item?.TRANSACTION_DESCRIPTION}</Paragraph>
              </Block>
            </>
          ) : (
            <></>
          )}
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_TRANG_THAI")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>{item?.status_name}</Paragraph>
          </Block>
        </Block>
      </TouchableRipple>
    </React.Fragment>
  );

  const renderMobile = (
    <FlatList data={dataTable} keyExtractor={(_item) => String(_item.id)} renderItem={renderItemDetail} initialNumToRender={10} />
  );

  return (
    <Block style={styles.displayShow}>
      {isDesktop ? (
        <DataTableView
          header={[
            {
              name: "#",
              attr: "id",
            },
            {
              name: t("ACC_NGAY_LAP_LENH"),
              attr: "FORMAT_TRANSACTION_DATE",
            },
            {
              name: t("ACC_SO_THAM_CHIEU"),
              attr: "RQSTSEQ",
            },
            {
              name: t("ACC_SO_TIEN"),
              attr: "CURRENCY_TRANSACTION_AMOUNT",
              isCustomDataColumn: true,
              type: Constant.DEBIT,
            },
            {
              name: t("ACC_PHI_GD"),
              attr: "CURRENCY_TOTAL_FEE_AMOUNT",
              isCustomDataColumn: true,
              type: Constant.FEE,
            },
            {
              name: t("ACC_MO_TA"),
              attr: "TRANSACTION_DESCRIPTION",
              notCenter: true,
            },
            {
              name: t("ACC_TK_NHAN"),
              attr: "CREDIT_ACCOUNT_NUMBER",
            },
            {
              name: t("ACC_TEN_NGUOI_NHAN"),
              attr: "CREDIT_ACCOUNT_NAME",
            },
            {
              name: t("ACC_NGAN_HANG_NHAN"),
              attr: "CREDIT_BANK_NAME",
            },
            {
              name: t("ACC_TRANG_THAI"),
              attr: "status_name",
            },
          ]}
          dataTable={dataTable}
          widthCell={!isDesktop ? 120 : undefined}
        />
      ) : (
        renderMobile
      )}
      {isShow && data.length === 0 ? (
        <EmptyBox title={t("ACC_KHONG_CO_GIAO_DICH_NAO")} />
      ) : isShow && data.length > 0 && dataTable.length === 0 ? (
        <EmptyBox title={t("COMMON_KHONG_TIM_THAY_TIM_KIEM")} />
      ) : (
        <></>
      )}
    </Block>
  );
}

export default TransactionTable;
